// libraries
import {
    FINANCE,
    LEASE,
    CASH,
    MongoDealer,
    MongoDealerCreditTier,
    IVehicleForPayment,
    AccelerateVehicleCondition,
    PaymentProcessingStrategies
} from '@makemydeal/dr-platform-types';

// consts/enums
import { INCLUDE_ONLY_TAXES, INCLUDE_TAXES_AND_STATE_FEES } from './types/consts';
import { CreditTierName, CreditTierType } from './types/enums';

export const getAverageCreditScore = (tier: MongoDealerCreditTier) => {
    return Math.round((tier.highScore + tier.lowScore) / 2);
};

/**
 * Find the dealer's default credit tier to present as selected to the shopper
 * If the dealer is misconfigured, fall back to the Excellent credit tier as it will
 * display the lowest payments
 * @param dealer the dealer object
 * @param vehicleIsNew is it a new vehicle
 * @returns MongoDealerCreditTier
 */
export const getDealerDefaultTier = (dealer: MongoDealer, vehicleIsNew: boolean): MongoDealerCreditTier => {
    const { creditTiers } = dealer;
    const creditTierType = vehicleIsNew ? CreditTierType.NEW : CreditTierType.USED;
    const fallbackExcellentTier = creditTiers.find(
        (tier) => tier.name === CreditTierName.EXCELLENT && tier.type === creditTierType
    ) as MongoDealerCreditTier;
    const configuredDefault = creditTiers.find((tier) => tier.isDefault && tier.type === creditTierType);
    const defaultTier = configuredDefault || fallbackExcellentTier;
    return defaultTier;
};

export const getExcludeSpecialAprPrograms = (dealer: MongoDealer, vehicleIsNew: boolean, offerType: string): boolean => {
    if (offerType === FINANCE) {
        return Boolean(
            vehicleIsNew
                ? dealer.incentives?.excludeSpecialAPRLoanOffersNewCar
                : dealer.incentives?.excludeSpecialAPRLoanOffersUsedCar
        );
    } else if (offerType === LEASE) {
        return Boolean(dealer.incentives?.excludeSpecialAPRLeaseOffersNewCar);
    }
    return false;
};

export const getExcludeStandardCashIncentives = (dealer: MongoDealer, offerType: string): boolean => {
    switch (offerType) {
        case FINANCE: {
            return Boolean(dealer.excludeStdCashIncsLoan);
        }
        case LEASE: {
            return Boolean(dealer.excludeStdCashIncsLease);
        }
        case CASH: {
            return Boolean(dealer.excludeStdCashIncsCash);
        }
    }
    return Boolean(dealer.excludeStdCashIncsLoan);
};

export const getFinanceDownPayment = (isNewVehicle: boolean, offerPrice: number, dealer: MongoDealer) => {
    if (dealer.financing.isDownPaymentFixedAmount) {
        if (isNewVehicle) {
            return dealer.financing.downPaymentFixedAmountNew || 0;
        } else {
            return dealer.financing.downPaymentFixedAmountUsed || 0;
        }
    } else {
        const downPaymentPercent = dealer.financing.downPaymentPercent || 0;
        if (offerPrice === 0 || downPaymentPercent === 0) {
            return 0;
        } else {
            return Math.round(offerPrice * (downPaymentPercent / 100));
        }
    }
};

export const getLeaseDownPayment = (offerPrice: number, dealer: MongoDealer) => {
    if (dealer.leasing.isDownPaymentFixedAmount) {
        return dealer.leasing.downPaymentFixedAmount;
    } else {
        const downPaymentPercent = dealer.leasing.downPaymentPercent || 0;
        if (offerPrice === 0 || downPaymentPercent === 0) {
            return 0;
        } else {
            return Math.round(offerPrice * (downPaymentPercent / 100));
        }
    }
};

export const getFinanceTermsList = (
    dealer: MongoDealer,
    vehicleIsNew: boolean,
    creditScore: number | undefined,
    defaultCreditTier: MongoDealerCreditTier
): number[] => {
    let tierForTerms = defaultCreditTier;
    if (creditScore) {
        tierForTerms = getCreditTierForCreditScore(creditScore, vehicleIsNew, dealer);
    }
    const financeTerms = tierForTerms.aprs.map(({ term }) => term);
    return financeTerms.sort();
};

export const getDealerApplyTaxesAndFeesNewOptions = (dealer: MongoDealer) => {
    return dealer.applyTaxesAndFeesNewOptions;
};

export const getIncludeGovernmentFees = (dealer: MongoDealer) => {
    const taxesAndFees = getDealerApplyTaxesAndFeesNewOptions(dealer);

    const shouldIncludeFees = Boolean(taxesAndFees === INCLUDE_TAXES_AND_STATE_FEES);

    return shouldIncludeFees || false;
};

export const isTaxesIncluded = (dealer: MongoDealer) => {
    const applyTaxesAndFeesNewOptions = getDealerApplyTaxesAndFeesNewOptions(dealer);
    const includeTaxes =
        applyTaxesAndFeesNewOptions === INCLUDE_TAXES_AND_STATE_FEES || applyTaxesAndFeesNewOptions === INCLUDE_ONLY_TAXES;
    return includeTaxes;
};

export const getShouldIncludeLease = (dealer: MongoDealer, vehicle: IVehicleForPayment) => {
    return isVehicleNew(vehicle) && dealer.products?.hasLeasing;
};

export const getNumericPrice = (price: number) => (price ? Math.abs(price) : 0);

export const getLeaseOfferPrice = (dealer: MongoDealer, vehicle: IVehicleForPayment) => {
    return dealer.leasing?.calculateOnMSRP && vehicle.retailPrice
        ? getNumericPrice(vehicle.retailPrice)
        : getNumericPrice(vehicle.listedPrice);
};

export const getLeaseTermsList = (dealer: MongoDealer): number[] => {
    const leaseTerm = dealer.leasing?.terms ? dealer.leasing.terms.map((term) => term.months) : [];
    return leaseTerm;
};

export const getLeaseMileageList = (dealer: MongoDealer): number[] => {
    const leaseMileage = dealer.leasing?.mileages ? dealer.leasing.mileages.map((mileage) => mileage.mileage) : [];
    return leaseMileage;
};

export const getCashOfferPrice = (dealer: MongoDealer, vehicle: IVehicleForPayment) => {
    const isCashPaymentOnMSRP = dealer.cash?.calculateOnMSRP || false;
    return isCashPaymentOnMSRP && vehicle.retailPrice ? getNumericPrice(vehicle.retailPrice) : getNumericPrice(vehicle.listedPrice);
};

export const getAnnualMiles = (dealer: MongoDealer, clientAnnualMiles?: number): number => {
    if (clientAnnualMiles) {
        const mileages = dealer.leasing?.mileages || [];
        const doesDealerSupportAnnualMiles = mileages.find((mileageSetting) => mileageSetting.mileage === clientAnnualMiles);

        if (doesDealerSupportAnnualMiles) {
            return clientAnnualMiles;
        }
    }
    const dealerDefaultAnnualMiles = getDefaultAnnualMiles(dealer);
    return dealerDefaultAnnualMiles;
};

export const getDefaultAnnualMiles = (dealer: MongoDealer): number => {
    const mileages = dealer.leasing?.mileages || [];
    if (!mileages || mileages.length === 0) {
        return 0;
    }

    const defaultMileage = mileages.find((miles) => miles.isDefault);
    return defaultMileage ? defaultMileage.mileage : 0;
};

export const getIsDueAtSigninAmountDeduct = (dealer: MongoDealer): boolean => {
    return Boolean(dealer.leasing?.isDueAtSigninAmountDeduct);
};

export const isVehicleNew = (vehicle: IVehicleForPayment) => vehicle.condition?.toLowerCase() === AccelerateVehicleCondition.NEW;

export const getShouldIncludeCash = (dealer: MongoDealer, isVehicleNew: boolean) => {
    return isVehicleNew ? dealer.products.hasNewCashOffer : dealer.products.hasUsedCashOffer;
};

/**
 * Accepts a credit score, vehicle, and dealer object
 * If the credit score aligns with a configured tier, return it
 * Otherwise the score is above or below the configured tiers,
 * so default to Excellent if it's over, or Poor if it's below
 * @param creditScore
 * @param isVehicleNew
 * @param dealer
 * @returns MongoDealerCreditTier
 */
export const getCreditTierForCreditScore = (
    creditScore: number,
    isVehicleNew: boolean,
    dealer: MongoDealer
): MongoDealerCreditTier => {
    const { creditTiers } = dealer;

    const creditTierType = isVehicleNew ? CreditTierType.NEW : CreditTierType.USED;
    const tiersForCondition = creditTiers.filter(({ type }) => type === creditTierType);

    let matchingTier = tiersForCondition.find(({ highScore, lowScore }) => creditScore >= lowScore && creditScore <= highScore);
    if (matchingTier) {
        return matchingTier;
    }

    // credit score can be below Poor low-end score, so we pick Poor as a fallback
    const poorTier = tiersForCondition.find(({ name }) => name === CreditTierName.POOR) as MongoDealerCreditTier;
    if (creditScore < poorTier.lowScore) {
        return poorTier;
    }

    return tiersForCondition.find(({ name }) => name === CreditTierName.EXCELLENT) as MongoDealerCreditTier;
};

export const getFinancePreferredTerm = (
    { financing: { defaultTermNew, defaultTermUsed } }: MongoDealer,
    isNewVehicle: boolean
): number => (isNewVehicle ? defaultTermNew : defaultTermUsed);

export const getLeasePreferredTerm = ({ leasing: { terms } }: MongoDealer): number =>
    terms.find(({ isDefault }) => isDefault)!.months;

export const getQuotesFiltrationStrategy = ({
    incentives: { enableMaxCashIncentives },
    enableLowestAPR
}: MongoDealer): PaymentProcessingStrategies => {
    if (enableLowestAPR) {
        return PaymentProcessingStrategies.lowestAPR;
    }

    if (enableMaxCashIncentives) {
        return PaymentProcessingStrategies.maxCashBack;
    }

    return PaymentProcessingStrategies.lowestMonthlyPayment;
};
